<template>
  <div class="DriverCalculationForm">
    <form @submit="onCalculate">
      <text-field
        :disabled="inputsDisabled"
        :value="driverCurrent"
        label="Current (mA)"
        type="number"
        :active="disableInput !== 'current'"
        @input="onDriverCurrentChange"
        @click="onDriverCurrentClick"
        :tooltipContent="
          'Specifying the current will use this field as an input,<br> otherwise it serves as an calculation output.'
        "
      >
      </text-field>
      <text-field
        :disabled="inputsDisabled"
        :value="driverVoltage"
        label="Voltage (V)"
        type="number"
        step="0.1"
        :active="disableInput !== 'voltage'"
        @input="onDriverVoltageChange"
        @click="onDriverVoltageClick"
        :tooltipContent="
          'Specifying the voltage will use this field as an input,<br> otherwise it serves as an calculation output.'
        "
      >
      </text-field>
      <text-field
        :disabled="inputsDisabled"
        :value="driverPower"
        label="Power (W)"
        type="number"
        step="0.1"
        @input="onDriverPowerChange"
        :tooltipContent="
          'Specifying the power will use this field as an input,<br> otherwise it serves as an calculation output.'
        "
      >
      </text-field>

      <div class="form-group" v-if="region === 'na'">
        <div class="custom-control custom-checkbox">
          <input
            type="checkbox"
            class="custom-control-input"
            id="performancePower"
            @change="onDriverPerformancePowerChange"
          />
          <label class="custom-control-label" for="performancePower">
            Performance Power
          </label>
          <tooltip
            :content="
              'Narrows results to drivers with THD < 20% and PF > 0.9 at the operating point.'
            "
          />
        </div>
      </div>

      <div v-if="!inputsDisabled" @click="clearValues" class="clear pb-4">
        Clear values
      </div>
      <button
        v-if="!inputsDisabled"
        type="submit"
        class="btn btn-secondary btn-block btn-sm"
      >
        Calculate
      </button>
    </form>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import { EventBus } from "@/eventbus";
import ChangedInputWithSolutionModal from "@/components/modals/ChangedInputWithSolutionModal";

export default {
  name: "DriverCalculationForm",
  modals: {
    ChangedInputWithSolutionModal
  },
  data: () => {
    return {
      inputLockModel: false
    };
  },
  computed: {
    ...mapState("brain", ["region"]),
    ...mapState("brain/drivers", [
      "driverCurrent",
      "driverVoltage",
      "driverPower",
      "inputLock",
      "driver"
    ]),
    ...mapGetters("brain/solutions", ["hasSolutions", "solution"]),
    ...mapGetters("brain/solutions", {
      activeSolution: "active"
    }),
    ...mapGetters("brain/ui", ["dontShowChangedInputModal"]),

    disableInput() {
      return this.inputLock === "voltage" ? "current" : "voltage";
    },
    inputsDisabled() {
      return this.hasSolutions && this.solution.module !== null;
    },
    solutionHasDriver() {
      return this.hasSolutions && this.solution.driver;
    }
  },

  methods: {
    onDriverCurrentChange(value) {
      this.$store.commit("brain/drivers/setDriverCurrent", value.target.value);
    },
    onDriverCurrentClick() {
      this.inputLockModel = "current";
    },
    onDriverVoltageChange(value) {
      this.$store.commit("brain/drivers/setDriverVoltage", value.target.value);
    },
    onDriverVoltageClick() {
      this.inputLockModel = "voltage";
    },
    onDriverPowerChange(value) {
      this.$store.commit("brain/drivers/setDriverPower", value.target.value);
    },
    onDriverPerformancePowerChange(event) {
      this.$store.commit(
        "brain/drivers/setDriverPerformancePower",
        event.target.checked
      );
      this.onCalculate(event, true);
    },

    onCalculate(event, dontShowModal = false) {
      event.preventDefault();
      this.$store.commit("brain/drivers/adjustDriverPower");
      if (
        this.solutionHasDriver &&
        !this.dontShowChangedInputModal &&
        !dontShowModal
      ) {
        this.$modalRouter.push({
          name: "ChangedInputWithSolutionModal",
          props: { show: true },
          model: "show",
          on: {
            overwriteCurrentSolution: () => {
              EventBus.$emit("brain/drivers/handleFilterChange", {
                done: () => {
                  this.$store
                    .dispatch("brain/solutions/add", {
                      type: "driver",
                      addNew: false,
                      data: this.driver
                    })
                    .then(() => {
                      EventBus.$emit(
                        "recalculate",
                        this.$router.currentRoute.name
                      );
                    });
                }
              });
            },
            addToNewSolution: () => {
              EventBus.$emit("brain/drivers/handleFilterChange", {
                done: () => {
                  this.$store
                    .dispatch("brain/solutions/add", {
                      type: "driver",
                      addNew: true,
                      data: this.driver
                    })
                    .then(() => {
                      EventBus.$emit(
                        "recalculate",
                        this.$router.currentRoute.name
                      );
                    });
                }
              });
            },
            undo: () => {
              // Reset driver calculation parameters to the last known working state
              this.$store.commit(
                "brain/drivers/resetDriverCalculationParameters"
              );
              EventBus.$emit("brain/drivers/handleFilterChange", {});
            }
          }
        });
      } else {
        EventBus.$emit("brain/drivers/handleFilterChange", {});
      }
    },
    clearValues() {
      this.$store.commit("brain/drivers/clearCalculatedPerformance");
    }
  },
  created() {
    this.inputLockModel = this.inputLock;
  },
  watch: {
    inputLock() {
      this.inputLockModel = this.inputLock;
    },
    inputLockModel: function(inputLock) {
      this.$store.commit("brain/drivers/setInputLock", inputLock);
    }
  }
};
</script>

<style lang="scss" scoped>
.DriverCalculationForm {
  padding: 0 1.5rem;
}
.custom-control-label {
  margin: 0 0 0.5rem 0;
}

.custom-control-label:after,
.custom-control-label:before {
  top: 0.15625rem;
  width: 1rem;
  height: 1rem;
}

.formGroupInput {
  border: 1px solid $mid-gray-600;
  &::placeholder {
    color: $mid-gray-600;
  }
}
.formGroupInputRadio {
  border: 1px solid $mid-gray-600;
  &::placeholder {
    color: $mid-gray-600;
  }
}

.form-text {
  color: $blue;
  text-decoration: underline;
  cursor: pointer;
  font-size: 0.9rem;
}

.btn-secondary,
.btn-outline-secondary {
  font-size: 1.1rem;
  font-weight: normal;
  cursor: pointer;
}

.clear {
  align-self: flex-end;
  color: $blue;
  text-decoration: underline;
  cursor: pointer;
}

.pb-4 {
  padding-bottom: 0 !important;
  margin-bottom: 15px !important;
}
</style>

<style lang="scss">
.DriverCalculationForm {
  .easy-tooltip {
    margin: 0 2px;
    float: right;
  }
}
</style>
